import { DataProxy } from '@apollo/client';
import { Checkbox, createStyles, makeStyles, TextField, TextFieldProps, Theme } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { FilterOptionsState } from '@material-ui/lab';
import Autocomplete, { AutocompleteProps } from '@material-ui/lab/Autocomplete';
import { FetchResult } from 'apollo-link';
import React, { ChangeEvent, useState } from 'react';

import {
  CreateLabelMutation,
  GetLabelsQuery,
  GetLabelsQueryVariables,
  useCreateLabelMutation,
} from '../../../graphql/gen/types';
import { GET_LABELS_QUERY, useGetLabelsQuery } from '../../../graphql/queries/GetLabels';
import { colorPalette } from '../../../theme';

interface Props {
  className?: string;
  selectedLabelIds?: string[];
  onLabelsListChanged: (labelIds: string[]) => void;
  hideSelectedLabels?: boolean;
  disableCreateNewLabel?: boolean;
  openOnFocus?: boolean;
  variant?: TextFieldProps['variant'];
  centeredLabel?: boolean;
  autocompleteProps?: Partial<AutocompleteProps<LocalLabel, true, false, false>>;
  inputProps?: Partial<TextFieldProps>;
}

export interface Label {
  name: string;
  id: string;
}

type LocalLabel = Label & { displayName?: string };

const prefixNewLabel = 'create_new_label';

export const LabelInput = React.memo((props: Props): React.ReactElement | null => {
  const { data } = useGetLabelsQuery();
  const {
    autocompleteProps,
    inputProps,
    openOnFocus,
    className,
    selectedLabelIds,
    onLabelsListChanged,
    hideSelectedLabels,
    disableCreateNewLabel,
    variant,
    centeredLabel,
  } = props;
  const [inputValue, setInputValue] = useState('');
  const [inputRef, setInputRef] = useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (inputRef && openOnFocus) {
      inputRef.focus();
    }
  }, [inputRef, openOnFocus]);

  const [createLabelMutation, { error }] = useCreateLabelMutation({
    update: (proxy: DataProxy, { data }: FetchResult<CreateLabelMutation>): void => {
      // Update cache on success
      const newLabel = data?.createLabel;
      if (!newLabel) {
        return;
      }
      try {
        const existingData: GetLabelsQuery | null = proxy.readQuery<GetLabelsQuery, GetLabelsQueryVariables>({
          query: GET_LABELS_QUERY,
        });
        const labels = [...(existingData?.loggedIn?.labels || []), newLabel];
        proxy.writeQuery<GetLabelsQuery, GetLabelsQueryVariables>({
          query: GET_LABELS_QUERY,
          data: {
            loggedIn: {
              ...(existingData?.loggedIn || {}),
              labels,
            },
          },
        });
        onLabelsListChanged([...(selectedLabelIds || []), newLabel.id]);
      } catch (e) {
        // exception from `readQuery` means data was not available in cache. This shouldn't normally happen.
      }
    },
  });
  const labels: LocalLabel[] = data?.loggedIn?.labels || [];
  const classes = useStyles(props);

  const icon = <CheckBoxOutlineBlankIcon className={classes.labelItemCheck} fontSize="small" />;
  const checkedIcon = <CheckBoxIcon className={classes.labelItemCheck} fontSize="small" />;
  const handleInputChange = (ev: ChangeEvent<unknown>, newInputValue: string): void => {
    if (!!ev) {
      setInputValue(newInputValue);
    }
  };

  if (error) {
    return <div>Error loading labels</div>;
  }

  return (
    <div className={className}>
      <Autocomplete
        {...autocompleteProps}
        multiple
        options={labels}
        value={labels.filter((l) => selectedLabelIds?.includes(l.id))}
        getOptionLabel={(option): string => option.name}
        classes={{ input: classes.input }}
        clearOnBlur={false}
        clearOnEscape={false}
        renderInput={(params): React.ReactNode => {
          return (
            <TextField
              {...params}
              {...inputProps}
              margin={'dense'}
              variant={variant || 'standard'}
              label={'Labels'}
              inputRef={(input: HTMLElement): void => setInputRef(input)}
              placeholder={'Search labels'}
              InputLabelProps={
                centeredLabel
                  ? {
                      classes: { root: classes.label, focused: classes.focusedLabel, shrink: classes.focusedLabel },
                    }
                  : undefined
              }
            />
          );
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={(_, selectedOptions): void => {
          const newLabelList = selectedOptions.filter((o) => o.id.startsWith(prefixNewLabel));
          if (newLabelList.length > 0) {
            createLabelMutation({ variables: { name: newLabelList[newLabelList.length - 1].name } });
          }

          if (!!onLabelsListChanged) {
            onLabelsListChanged(selectedOptions.filter((o) => !o.id.startsWith(prefixNewLabel)).map((o) => o.id));
          }
        }}
        renderOption={(option, { selected }): React.ReactNode => (
          <React.Fragment>
            {!option.id.startsWith(prefixNewLabel) && (
              <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
            )}
            {option.id.startsWith(prefixNewLabel) ? option.displayName : option.name}
          </React.Fragment>
        )}
        filterOptions={(options: LocalLabel[], params: FilterOptionsState<LocalLabel>): LocalLabel[] => {
          // suggest creating the label
          const inputValue = params.inputValue;
          if (!!inputValue) {
            const matchingOptions = options.filter((option) => option.name.startsWith(inputValue));
            if (!disableCreateNewLabel && !options.map((option) => option.name).includes(inputValue)) {
              matchingOptions.push({
                id: `${prefixNewLabel}_${inputValue}`,
                displayName: `Create new label "${inputValue}"`,
                name: inputValue,
              });
            }
            return matchingOptions;
          }
          return options;
        }}
        ChipProps={
          hideSelectedLabels
            ? {
                classes: {
                  root: classes.hidden,
                },
              }
            : {}
        }
      />
    </div>
  );
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    labelItemCheck: {
      marginRight: 8,
      color: colorPalette.primaryColorGreen.primary,
    },
    input: {
      height: 30,
    },
    label: {
      marginTop: 5,
    },
    focusedLabel: {
      marginTop: 0,
    },
    hidden: {
      display: 'none',
    },
  })
);
